/**
 * useJourneyPolicies Hook
 * 
 * Hook per accesso alle policy/regole degli elementi del viaggio.
 * Fornisce informazioni consultative su modifica, cancellazione, eccezioni.
 * 
 * FASE ATTUALE: Solo consultazione
 * NON implementa:
 * - Richieste di modifica operative
 * - Cancellazioni reali
 * - Enforcement automatico
 * 
 * PRINCIPIO:
 * - Policy = cosa SI PUÒ fare
 * - Stato = a che punto È l'elemento
 * - NON confondere policy con stati
 */

import { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Hook per gestire le policy del journey
 * @param {string} code - Codice del journey/travel pass
 * @returns {Object} Dati policy e helper functions
 */
export function useJourneyPolicies(code) {
  const [policiesData, setPoliciesData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch policies
  const fetchPolicies = useCallback(async () => {
    if (!code) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_URL}/api/journey/${code}/policies`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch policies: ${response.status}`);
      }

      const data = await response.json();
      setPoliciesData(data);
    } catch (err) {
      console.error('Error fetching journey policies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  // Memoized policies list
  const policies = useMemo(() => {
    return policiesData?.policies || [];
  }, [policiesData]);

  // Memoized policies by item id
  const policiesByItem = useMemo(() => {
    const byItem = {};
    for (const p of policies) {
      if (p.item_id) {
        byItem[p.item_id] = p;
      }
    }
    return byItem;
  }, [policies]);

  // Get policy for a specific item
  const getPolicyForItem = useCallback((itemId) => {
    return policiesByItem[itemId] || null;
  }, [policiesByItem]);

  // Get policy summary for UI display
  const getPolicySummary = useCallback((itemId) => {
    const itemPolicy = policiesByItem[itemId];
    if (!itemPolicy) {
      return {
        modification: { label: 'N/D', color: '' },
        cancellation: { label: 'N/D', color: '' },
        post_voucher: { label: 'N/D' },
        exceptions: { label: 'N/D' },
        user_note: null
      };
    }
    return itemPolicy.policy;
  }, [policiesByItem]);

  // Check if modification is possible for an item
  const canModify = useCallback((itemId) => {
    const policy = getPolicySummary(itemId);
    const label = policy.modification?.label || '';
    return label === 'Consentita' || label === 'Su richiesta';
  }, [getPolicySummary]);

  // Check if cancellation is possible for an item
  const canCancel = useCallback((itemId) => {
    const policy = getPolicySummary(itemId);
    const label = policy.cancellation?.label || '';
    return label !== 'Non consentita' && label !== 'N/D';
  }, [getPolicySummary]);

  // Get modification window hours for an item
  const getModificationWindow = useCallback((itemId) => {
    const policy = getPolicySummary(itemId);
    return policy.modification?.hours || null;
  }, [getPolicySummary]);

  // Get cancellation window hours for an item
  const getCancellationWindow = useCallback((itemId) => {
    const policy = getPolicySummary(itemId);
    return policy.cancellation?.hours || null;
  }, [getPolicySummary]);

  return {
    // Raw data
    policiesData,
    loading,
    error,

    // Processed data
    policies,
    policiesByItem,

    // Helpers
    getPolicyForItem,
    getPolicySummary,
    canModify,
    canCancel,
    getModificationWindow,
    getCancellationWindow,

    // Actions
    reload: fetchPolicies
  };
}

export default useJourneyPolicies;
